/**
 * Zoom+Pan der PDF-Vorschau als geteilter Hook (Thomas 24.07.: „wir brauchen
 * wieder die Zoom-Funktionalität — existiert schon, für Zulagen und
 * Fahrtenbuch einbauen"). Quelle der Semantik: PdfWithHighlights Phase 3
 * (Stundenliste) — Ctrl/Cmd+Mausrad zoomt 1–4×, ab Zoom >1 zieht man das
 * Bild (Pointer-Drag), ⟲ setzt zurück. Der Aufrufer legt Bild + Overlay-
 * Ebene GEMEINSAM in einen Wrapper mit `transform` — so bleiben Markierungen
 * beim Zoomen deckungsgleich (CSS-Transform ändert kein Layout, die
 * ResizeObserver-Geometrie bleibt gültig).
 * PdfWithHighlights selbst bleibt unangetastet (kein Regressions-Risiko auf
 * der Stundenliste); neue Views nutzen diesen Hook.
 */
import { useRef, useState } from "react";
import type React from "react";

export function usePdfZoomPan() {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  const clampZoom = (z: number) => Math.max(1, Math.min(4, z));
  // Thomas 24.07.: Mausrad zoomt DIREKT (ohne Strg) — die Vorschau ist eine
  // eigene Arbeitsfläche, dort ist Zoomen die erwartete Rad-Aktion. Der Zoom
  // ankert am CURSOR (der Punkt unter der Maus bleibt stehen) — so "faehrt"
  // man mit Rad+Ziehen natuerlich durch den Beleg. Zurueck auf 100% -> Pan 0.
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const nz = clampZoom(zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
    if (nz === zoom) return;
    if (nz <= 1.01) {
      setPan({ x: 0, y: 0 });
    } else {
      const cx = (px - pan.x) / zoom;
      const cy = (py - pan.y) / zoom;
      setPan({ x: px - cx * nz, y: py - cy * nz });
    }
    setZoom(nz);
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if (zoom <= 1.01) return;
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.startPanX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.startPanY + (e.clientY - dragRef.current.startY),
    });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current) {
      try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* */ }
      dragRef.current = null;
    }
  };
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;

  return { zoom, setZoom, clampZoom, pan, transform, onWheel, onPointerDown, onPointerMove, onPointerUp, resetZoom };
}
